import { useLoaderData, useSearchParams } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { IN_PAGE_SEARCH_PARAM_KEY } from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useTabLoadMore } from '@modules/professional/hook/use-tab-load-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import {
    useViewportPageSync,
    viewportPageMarkerProps,
} from '~/ui-rocco/hooks/use-viewport-page-sync';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import {
    detailCta,
    DetailTileMedia,
    detailTileActionsFor,
} from '~/ui-rocco/pages/detail-shared';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { getArticleGridLayout } from '../utils/article-grid-layout';
import { TAB_CONTENT_GAP } from '../utils/tab-spacing';

/**
 * §8 — (04) Articles. Mirrors the logged-out homepage ArticlesSection's grid:
 * the numbered section label above a
 * magazine grid — one large feature card beside a 2×2 of smaller cards — with
 * Gestura Headline (`font-gestura`) serif titles. Self-hides when empty.
 */
interface ArticleCardProps {
    id?: number;
    image?: string;
    title?: string;
    category?: string;
    date?: string;
    readTime?: string;
    link?: string;
    size: 'large' | 'small';
    /**
     * Whose articles these are. Every card on this page belongs to the
     * professional whose page it is, so the panel can offer their site and an
     * enquiry — without it a reader who likes the work has nothing to do about
     * it but save the picture.
     */
    professional: ArticleCardProfessional;
    /** Extra classes for the tile root — e.g. `md:h-full` to fill a stretched grid cell. */
    className?: string;
    /** Extra classes for the media box, applied after its default aspect ratio. */
    mediaClassName?: string;
}

/** The page's own professional, as the article cards need them. */
interface ArticleCardProfessional {
    id: number;
    name: string;
    link: string;
    website?: string;
}

const ArticleCard = ({
    id,
    image,
    title,
    category,
    date,
    readTime,
    link,
    size,
    professional,
    className,
    mediaClassName,
}: ArticleCardProps) => {
    if (!title) return null;
    const meta = [date, readTime].filter(Boolean).join(', ');
    const actions = detailTileActionsFor({
        id: id ?? 0,
        link: link ?? '/articles',
        title,
        images: image ? [{ src: image }] : [],
        pinType: id ? 'ArticlePin' : undefined,
        professional: { id: professional.id, name: professional.name },
        professionalLink: professional.link,
        website: professional.website,
    });
    return (
        <div className={cn('relative', className)}>
            <RoccoLink
                to={link ?? '/articles'}
                className="flex h-full flex-col"
            >
                <DetailTileMedia
                    src={image}
                    alt={title}
                    className={cn(
                        'bg-neutral-200',
                        size === 'large'
                            ? 'aspect-[855/597]'
                            : 'aspect-[398/489]',
                        mediaClassName,
                    )}
                    imgClassName="max-w-none"
                    pinId={id}
                    pinType={id ? 'ArticlePin' : undefined}
                    enquiry={actions.enquiry}
                    visitWebsite={actions.visitWebsite}
                    share={actions.share}
                    tileInfo={actions.tileInfo}
                />
                <p className="m-0 font-gestura leading-[1.14] font-normal tracking-[-0.01em] text-[#1f1f21] capitalize max-md:pt-3 max-md:text-base md:pt-[1.248rem] md:text-[1.7496rem]">
                    {title}
                </p>
                {category && (
                    <p className="m-0 font-sans font-medium tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-2 max-md:text-xs md:pt-[0.996rem] md:text-[0.75rem]">
                        {category}
                    </p>
                )}
                {meta && (
                    <p className="m-0 font-sans font-normal tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-0.5 max-md:text-xs md:pt-[0.252rem] md:text-[0.75rem]">
                        {meta}
                    </p>
                )}
            </RoccoLink>
            {/* Mobile actions live outside the tile link so a tap on the `•••`
            can never navigate. */}
            {id ? (
                <TileMobileActionsSlot
                    pinId={id}
                    pinType="ArticlePin"
                    enquiry={actions.enquiry}
                    visitWebsite={actions.visitWebsite}
                    share={actions.share}
                    tileInfo={actions.tileInfo}
                />
            ) : null}
        </div>
    );
};

interface ProfessionalArticlesSectionProps {
    /**
     * `overview` (default): the numbered "(0N — Articles)" Discover header used
     * on the professional overview page. `tab`: the standalone Articles-tab
     * header — a big "Articles" title + intro, then an "(All Articles)" divider.
     */
    variant?: 'overview' | 'tab';
    /** Overview-only: this section's position in the page's "(0N)" sequence. */
}

export const ProfessionalArticlesSection = ({
    variant = 'overview',
}: ProfessionalArticlesSectionProps = {}) => {
    const { articleTiles, articlesHasMore } = useLoaderData<typeof loader>();
    const routeProfessional = useProfessionalInitialRouteData();
    const { URLSegment } = routeProfessional;
    const cardProfessional = {
        id: routeProfessional.ID,
        name: routeProfessional.Title,
        link: routeProfessional.Link,
        website: routeProfessional.Website,
    };
    const { desktop } = useAppDisplayModeContext();
    const [searchParams] = useSearchParams();
    const isTab = variant === 'tab';
    const hasActiveSearch = !!searchParams.get(IN_PAGE_SEARCH_PARAM_KEY);

    // Infinite scroll powers the standalone Articles tab; the overview variant
    // keeps its fixed 5-card magazine layout, so it never appends pages.
    const {
        items: loadedArticles,
        hasMore,
        isLoading,
        loadMore,
        pageStartByIndex,
    } = useTabLoadMore({
        initialItems: articleTiles ?? [],
        initialHasMore: isTab && articlesHasMore,
        initialPage: 1,
        urlSegment: URLSegment,
        searchType: 'blogpost',
        getKey: (article) => article.id,
    });

    // `?page=N` follows the page of articles actually in view, scrolling
    // either direction — not the pages the infinite-scroll lookahead fetched.
    useViewportPageSync({
        enabled: isTab && (hasMore || Object.keys(pageStartByIndex).length > 1),
    });

    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    // Overview self-hides when the pro has no articles. The tab keeps its
    // header + search bar only for a 0-result *search* (so it can be cleared);
    // with no search and nothing to show it self-hides too, mirroring the
    // Network tab — otherwise a pro with no journal content would see a
    // misleading "try changing the search term".
    if (loadedArticles.length === 0 && !(isTab && hasActiveSearch)) return null;
    // Magazine hero = 1 feature + its portrait tiles; the tab spills the rest
    // into a uniform continuation grid that infinite scroll keeps growing.
    const { heroCount, isWideHero } = getArticleGridLayout(
        loadedArticles.length,
        isTab,
    );
    const [feature, ...rest] = loadedArticles.slice(0, heroCount);
    const continuation = isTab ? loadedArticles.slice(heroCount) : [];

    return (
        <section className="w-full overflow-hidden bg-ground text-[#1f1f21]">
            {isTab ? (
                // Desktop keeps the editorial title + intro + "(All Articles)"
                // divider; on mobile the tab goes straight to the search + grid.
                desktop && (
                    <>
                        <div
                            className={cn(
                                'mx-auto w-full max-w-[120rem] md:px-[3.132rem]',
                                TAB_CONTENT_GAP,
                            )}
                        >
                            {/* The heading repeated the tab pill above it; kept as sr-only
                                so the tab still has a heading in the document outline. */}
                            <h2 className="sr-only">Articles</h2>
                        </div>
                        <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem]">
                            {/* Desktop only: on mobile this is the first thing
                                under the tab strip, whose own border already
                                closes off the nav, so a second rule a hairline
                                below it reads as one thick line. */}
                            <div className="h-px w-full bg-black/20 max-md:hidden" />
                        </div>
                    </>
                )
            ) : (
                /* The statement's own voice, as it is set under the banner:
                   the section is named rather than numbered, so it reads as a
                   line of the page rather than an entry in a contents list. */
                <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-5 md:px-[3.132rem] md:pt-[1.875rem]">
                    <h2 className="m-0 font-sans font-medium tracking-[-0.025em] text-black max-md:text-[2rem] max-md:leading-[1.08] md:text-[3rem] md:leading-[1.05]">
                        Articles
                    </h2>
                </div>
            )}

            {loadedArticles.length === 0 ? (
                <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-[2.4rem] max-md:pb-14 md:px-[3.132rem] md:pt-[2.4rem] md:pb-[7.5rem]">
                    <NoSearchResults resultsType="articles" />
                </div>
            ) : (
                <div
                    className={cn(
                        'mx-auto grid w-full max-w-[120rem] grid-cols-2 max-md:gap-x-2 max-md:gap-y-6 max-md:px-6 max-md:pt-[2.4rem] md:grid-cols-12 md:gap-x-6 md:gap-y-[5.9375rem] md:px-[3.132rem]',
                        isTab ? 'md:pt-[1.5rem]' : 'md:pt-[3rem]',
                    )}
                >
                    {feature && (
                        <div
                            className={cn(
                                'max-md:col-span-2 md:col-span-6',
                                isWideHero ? 'md:h-full' : 'md:row-span-2',
                            )}
                            // The feature opens page 1 — the viewport page
                            // sync needs a lowest-page marker to clear
                            // `?page=N` when the reader scrolls back up.
                            // Tab-only: the overview variant never pages.
                            {...(isTab ? viewportPageMarkerProps(1) : {})}
                        >
                            <ArticleCard
                                id={feature.id}
                                image={feature.image}
                                title={feature.title}
                                category={feature.label}
                                date={feature.date}
                                readTime={feature.readTime}
                                link={feature.link}
                                size="large"
                                professional={cardProfessional}
                                // The feature's landscape art is taller than the
                                // portrait tiles beside it, so in the wide hero
                                // its media fills the row instead of setting its
                                // own height — the row is sized by those tiles,
                                // so both bottom out level at every width.
                                className={cn(isWideHero && 'md:h-full')}
                                mediaClassName={cn(
                                    isWideHero &&
                                        'md:aspect-auto md:min-h-0 md:flex-1',
                                )}
                            />
                        </div>
                    )}
                    {rest.map((article, index) => (
                        <div
                            key={article.id ?? index}
                            className="md:col-span-3"
                        >
                            <ArticleCard
                                id={article.id}
                                image={article.image}
                                title={article.title}
                                category={article.label}
                                date={article.date}
                                readTime={article.readTime}
                                link={article.link}
                                size="small"
                                professional={cardProfessional}
                            />
                        </div>
                    ))}
                </div>
            )}

            {continuation.length > 0 && (
                <div className="mx-auto grid w-full max-w-[120rem] grid-cols-2 max-md:gap-x-2 max-md:gap-y-6 max-md:px-6 max-md:pt-6 md:grid-cols-4 md:gap-x-6 md:gap-y-[5.9375rem] md:px-[3.132rem] md:pt-[10rem]">
                    {continuation.map((article, index) => {
                        const startPage = pageStartByIndex[heroCount + index];
                        return (
                            <div
                                key={article.id ?? index}
                                {...(startPage === undefined
                                    ? {}
                                    : viewportPageMarkerProps(startPage))}
                            >
                                <ArticleCard
                                    id={article.id}
                                    image={article.image}
                                    title={article.title}
                                    category={article.label}
                                    date={article.date}
                                    readTime={article.readTime}
                                    link={article.link}
                                    size="small"
                                    professional={cardProfessional}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {isTab && infiniteEnabled && hasMore && (
                <div
                    ref={sentinelRef}
                    aria-hidden="true"
                    style={{ height: 1 }}
                />
            )}
            {isTab && showViewMore && (
                <div className="flex justify-center pt-[3rem] pb-[3.6rem]">
                    <button
                        type="button"
                        onClick={onViewMore}
                        className={detailCta(
                            'outlined-light',
                            'max-md:h-[3.125rem] max-md:text-sm',
                        )}
                    >
                        View more
                    </button>
                </div>
            )}
            {isTab && isLoading && (
                <div className="flex justify-center pt-[3rem] pb-[3.6rem]">
                    <Spinner className="size-8 text-[#181818]" />
                </div>
            )}
        </section>
    );
};
